import * as fs from 'fs'
import * as path from 'path'
import * as os from 'os'

export interface OrganizationInfo {
  id: string
  name: string
}

export interface FlightDeskConfig {
  // Single API key for the user
  apiKey?: string
  // Currently active organization
  activeOrganization?: string
  // All organizations the user belongs to
  organizations: OrganizationInfo[]
  // Repo to project mappings
  repoMapping: Record<string, string>
}

const CONFIG_FILE = path.join(os.homedir(), '.flightdeskrc')
const DEFAULT_API_URL = 'https://api.flightdesk.dev'
const DEV_API_URL = 'http://localhost:3000'

// Global API URL override (set by --dev or --api options)
let apiUrlOverride: string | null = null

export function setDevMode(enabled: boolean): void {
  if (enabled) {
    apiUrlOverride = DEV_API_URL
  }
}

export function setApiUrl(url: string): void {
  apiUrlOverride = url
}

export function getApiUrl(): string {
  // Always use production URL unless --dev or --apiUrl flag is passed
  // API URL is never persisted in config
  return apiUrlOverride ?? DEFAULT_API_URL
}

export function loadConfig(): FlightDeskConfig {
  try {
    if (fs.existsSync(CONFIG_FILE)) {
      const content = fs.readFileSync(CONFIG_FILE, 'utf-8')
      const parsed = JSON.parse(content)

      return {
        organizations: [],
        repoMapping: {},
        ...parsed,
      }
    }
  } catch (error) {
    console.error('Warning: Failed to load config file:', error)
  }

  return {
    organizations: [],
    repoMapping: {},
  }
}

export function saveConfig(config: FlightDeskConfig): void {
  fs.writeFileSync(CONFIG_FILE, JSON.stringify(config, null, 2))
}

export function getActiveOrganization(): OrganizationInfo | null {
  const config = loadConfig()

  if (!config.activeOrganization) {
    // Return first org if only one exists
    if (config.organizations.length === 1) {
      return config.organizations[0]
    }
    return null
  }

  return config.organizations.find(o => o.id === config.activeOrganization) || null
}

export function setActiveOrganization(orgId: string): void {
  const config = loadConfig()

  const org = config.organizations.find(o => o.id === orgId || o.name.toLowerCase() === orgId.toLowerCase())
  if (!org) {
    throw new Error(`Organization not found: ${orgId}`)
  }

  config.activeOrganization = org.id
  saveConfig(config)
}

export function updateOrganizations(orgs: OrganizationInfo[]): void {
  const config = loadConfig()
  config.organizations = orgs

  // If active org no longer exists, clear it
  if (config.activeOrganization && !orgs.some(o => o.id === config.activeOrganization)) {
    config.activeOrganization = orgs[0]?.id
  }

  saveConfig(config)
}

export function updateRepoMapping(repo: string, orgId: string): void {
  const config = loadConfig()
  config.repoMapping[repo] = orgId
  saveConfig(config)
}

/**
 * Get organization by repository name (from repo mapping)
 */
export function getOrganizationByRepo(repoFullName: string): OrganizationInfo | null {
  const config = loadConfig()
  const orgId = config.repoMapping[repoFullName]
  if (!orgId) return null
  return config.organizations.find(o => o.id === orgId) || null
}

export function isConfigured(): boolean {
  const config = loadConfig()
  return !!config.apiKey && config.organizations.length > 0
}

export function requireConfig(): FlightDeskConfig {
  const config = loadConfig()

  if (!config.apiKey) {
    console.error('❌ FlightDesk is not configured. Run: flightdesk init')
    process.exit(1)
  }

  if (config.organizations.length === 0) {
    console.error('❌ No organizations found. Run: flightdesk init')
    process.exit(1)
  }

  return config
}

export function requireActiveOrg(): { config: FlightDeskConfig; org: OrganizationInfo } {
  const config = requireConfig()
  const org = getActiveOrganization()

  if (!org) {
    console.error('❌ No active organization. Run: flightdesk org switch <org-name>')
    console.log('\nAvailable organizations:')
    config.organizations.forEach(o => console.log(`  - ${o.name}`))
    process.exit(1)
  }

  return { config, org }
}

export { CONFIG_FILE, DEFAULT_API_URL, DEV_API_URL }
